import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "layout__wrapper" }
const _hoisted_2 = { class: "search" }
const _hoisted_3 = { class: "container" }
const _hoisted_4 = { class: "search__form-box" }
const _hoisted_5 = { class: "search__form-group" }
const _hoisted_6 = { class: "search__input-box" }
const _hoisted_7 = {
  key: 7,
  class: "search__no-results"
}

import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'
import type { ComponentPublicInstance, Raw } from 'vue'
import { useRoute } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import Layout from '@/app/components/Layout.vue'
import CategoriesSelection from '@/app/components/listing/categories/CategoriesSelection.vue'
import BooksBlock from '@/app/components/listing/BooksBlock.vue'
import ArticlesBlock from '@/app/components/listing/ArticlesBlock.vue'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import { Sentry } from '@/services/sentry'

import { SearchView } from '@/models/search'

import { assertNotNull } from '@/helpers/typing'
import HeaderRoot from '@/app/components/header/Root.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import NoSearchResults from '@/app/components/doc/search/NoSearchResults.vue'
import { setFocus } from '@/helpers/form'
import { platform } from '@/services/platform'
import EpisodesBlock from '@/app/components/listing/EpisodesBlock.vue'
import PodcastsBlock from '@/app/components/listing/PodcastsBlock.vue'

import BooksBlockSkeleton from '@/app/components/listing/skeleton-loaders/BooksBlockSkeleton.vue'
import ArticlesBlockSkeleton from '@/app/components/listing/skeleton-loaders/ArticlesBlockSkeleton.vue'
import PodcastsBlockSkeleton from '@/app/components/listing/skeleton-loaders/PodcastsBlockSkeleton.vue'
import PodcastEpisodesBlockSkeleton from '@/app/components/listing/skeleton-loaders/PodcastEpisodesBlockSkeleton.vue'
import TagsBlockSkeleton from '@/app/components/listing/skeleton-loaders/TagsBlockSkeleton.vue'

import EmptyPodcastsBlock from '@/app/components/listing/EmptyPodcastsBlock.vue'
import { backend } from '@/services/backend'


export default /*@__PURE__*/_defineComponent({
  __name: 'Search',
  setup(__props) {

const route = useRoute()
const wait = useWait()

const searchInput = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const view = ref<Raw<SearchView> | null>(null)
const isInitialLoad = ref(true)

const showBooksSkeleton = computed(() => {
  if (!view.value) {
    return false
  }
  return !!view.value.search && (wait.is('Books') || view.value.isSearching)
})

const showBooksBlock = computed(() => {
  if (!view.value?.booksListView) {
    return false
  }
  if (!view.value.search) {
    return true
  }
  return !wait.is('Books') && !view.value.isSearching
})

const publicInstance = {
  wait,
  // We have to cast the type here because Vue unwraps
  // refs when they are passed to reactive elements (such as
  // `view`, which is where this object is used).
  searchInput: searchInput as any as ComponentPublicInstance | null,
}

onBeforeMount(async () => {
  wait.start('Loading Search')

  await loadSearch()

  // Autofocus search field on Desktop
  // We don't focus on mobile because soft keyboard takes a lot of space
  if (platform.isDesktopWidth() && searchInput.value) {
    nextTick(() => {
      setFocus(searchInput.value?.$el)
    })
  }

  // Initialize event listeners in `nextTick`, after all elements are rendered.
  nextTick(() => {
    // We don't have view initialized if loadSearch() failed.
    if (view.value) {
      view.value.initEventListeners()
    }
  })
})

onBeforeUnmount(() => {
  view.value?.clearEventListeners()
})

watch(
  () => view.value?.search,
  (newSearch, oldSearch) => {
    // Only set isSearching on user input, not on initial load
    if (view.value && newSearch !== oldSearch && !isInitialLoad.value) {
      view.value.isSearching = !!newSearch
    }
    return assertNotNull(view.value).searchHandler()
  },
)

async function loadSearch(): Promise<void> {
  try {
    const searchTerm = route.query.term as string

    const docsUserData = await backend.getDocsUserData()
    view.value = new SearchView(publicInstance, searchTerm, docsUserData)

    await view.value.init()

    // Mark initial load as complete
    isInitialLoad.value = false

    wait.end('Loading Search')
  } catch (error: any) {
    wait.start('Error Loading Search')
    Sentry.captureException(error)
  }
}

/**
 * Submit handler.
 *
 * We need to wrap an input into <form> to have a special
 * "Search" version of the soft keyboard on mobile.
 *
 * When user submits the form (clicks "Search" on soft keyboard)
 * we need to hide the keyboard.
 */
function formSubmit(): void {
  searchInput.value?.$el.blur()
}

return (_ctx: any,_cache: any) => {
  const _component_v_wait = _resolveComponent("v-wait")!

  return (_openBlock(), _createBlock(Layout, { class: "search" }, {
    default: _withCtx(() => [
      _createVNode(_Transition, { name: "search" }, {
        default: _withCtx(() => [
          _createVNode(HeaderRoot, {
            title: "Search",
            class: _normalizeClass([view.value?.headerClass, "search__header"])
          }, null, 8, ["class"])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_v_wait, { for: "Error Loading Search" }, {
          waiting: _withCtx(() => [
            _createVNode(LoadingError)
          ]),
          _: 1
        }),
        _createVNode(_component_v_wait, {
          class: _normalizeClass([
          'layout__wrapper-inner',
          { 'layout__wrapper-inner--search-results': !!view.value?.search },
        ]),
          for: "Loading Search"
        }, {
          waiting: _withCtx(() => [
            _createVNode(LoadingSpinner)
          ]),
          default: _withCtx(() => [
            (view.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["search-box", { 'search-box--results': !view.value.hasFocus || view.value.search }])
                }, [
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["search__top-box", { 'search__top-box--result': view.value.search }])
                    }, [
                      _createElementVNode("div", _hoisted_3, [
                        _cache[9] || (_cache[9] = _createElementVNode("h1", { class: "search__title" }, "Search", -1)),
                        _createElementVNode("div", _hoisted_4, [
                          _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, [
                              _cache[8] || (_cache[8] = _createElementVNode("span", { class: "search__icon iconfont isearch" }, null, -1)),
                              _createElementVNode("form", {
                                action: "",
                                onSubmit: _withModifiers(formSubmit, ["prevent","stop"])
                              }, [
                                _createVNode(InputWithContextMenu, {
                                  ref_key: "searchInput",
                                  ref: searchInput,
                                  modelValue: view.value.search,
                                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((view.value.search) = $event)),
                                  class: "search__input",
                                  placeholder: view.value.prompt,
                                  type: "search",
                                  onKeydown: _cache[1] || (_cache[1] = ($event: any) => (view.value.clearSearchOnEscape($event))),
                                  onInput: _cache[2] || (_cache[2] = ($event: any) => ($event.target.composing = false)),
                                  onFocus: _cache[3] || (_cache[3] = ($event: any) => (view.value.toggleFocus())),
                                  onBlur: _cache[4] || (_cache[4] = ($event: any) => (view.value.toggleFocus()))
                                }, null, 8, ["modelValue", "placeholder"]),
                                _withDirectives(_createElementVNode("div", {
                                  class: "search__reset",
                                  onClick: _cache[5] || (_cache[5] = ($event: any) => (view.value.search = ''))
                                }, [...(_cache[7] || (_cache[7] = [
                                  _createElementVNode("span", {
                                    class: "search__reset-icon iconfont iclose-1",
                                    type: "reset"
                                  }, null, -1)
                                ]))], 512), [
                                  [_vShow, view.value.search]
                                ])
                              ], 32)
                            ]),
                            (!view.value.hasFocus || view.value.search)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: "search__cancel-button",
                                  onClick: _cache[6] || (_cache[6] = ($event: any) => (view.value.search = ''))
                                }, "Cancel"))
                              : _createCommentVNode("", true)
                          ])
                        ])
                      ])
                    ], 2)
                  ])
                ], 2))
              : _createCommentVNode("", true),
            (view.value)
              ? (_openBlock(), _createBlock(_component_v_wait, {
                  key: 1,
                  for: "Tags"
                }, {
                  waiting: _withCtx(() => [
                    (view.value.showTags)
                      ? (_openBlock(), _createBlock(TagsBlockSkeleton, {
                          key: 0,
                          class: "container search__skeleton search__skeleton--tags"
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  default: _withCtx(() => [
                    (view.value.showTags && view.value.hasTags())
                      ? (_openBlock(), _createBlock(CategoriesSelection, {
                          key: 0,
                          view: view.value.tagsView,
                          class: _normalizeClass([
              'container',
              { search__categories: !view.value.hasFocus || view.value.search },
            ])
                        }, null, 8, ["view", "class"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (view.value)
              ? (_openBlock(), _createBlock(_component_v_wait, {
                  key: 2,
                  for: "Books"
                }, {
                  waiting: _withCtx(() => [
                    _createVNode(BooksBlockSkeleton, { class: "container search__skeleton search__skeleton--books" })
                  ]),
                  default: _withCtx(() => [
                    (showBooksSkeleton.value)
                      ? (_openBlock(), _createBlock(BooksBlockSkeleton, {
                          key: 0,
                          class: "container search__skeleton search__skeleton--books"
                        }))
                      : _createCommentVNode("", true),
                    (showBooksBlock.value && view.value.booksListView)
                      ? (_openBlock(), _createBlock(BooksBlock, {
                          key: 1,
                          view: view.value.booksListView,
                          class: "search__books container"
                        }, null, 8, ["view"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (view.value)
              ? (_openBlock(), _createBlock(_component_v_wait, {
                  key: 3,
                  for: "Articles"
                }, {
                  waiting: _withCtx(() => [
                    _createVNode(ArticlesBlockSkeleton, { class: "container search__skeleton search__skeleton--articles" })
                  ]),
                  default: _withCtx(() => [
                    (view.value.articlesListView)
                      ? (_openBlock(), _createBlock(ArticlesBlock, {
                          key: 0,
                          view: view.value.articlesListView,
                          class: "search__articles container"
                        }, null, 8, ["view"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (view.value)
              ? (_openBlock(), _createBlock(_component_v_wait, {
                  key: 4,
                  for: "Podcasts"
                }, {
                  waiting: _withCtx(() => [
                    _createVNode(PodcastsBlockSkeleton, { class: "container search__skeleton search__skeleton--podcasts" })
                  ]),
                  default: _withCtx(() => [
                    (view.value.podcastsListView)
                      ? (_openBlock(), _createBlock(PodcastsBlock, {
                          key: 0,
                          class: "container",
                          view: view.value.podcastsListView
                        }, null, 8, ["view"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (view.value)
              ? (_openBlock(), _createBlock(_component_v_wait, {
                  key: 5,
                  for: "Episodes"
                }, {
                  waiting: _withCtx(() => [
                    _createVNode(PodcastEpisodesBlockSkeleton, { class: "container search__skeleton search__skeleton--episodes" })
                  ]),
                  default: _withCtx(() => [
                    (view.value.episodesListView)
                      ? (_openBlock(), _createBlock(EpisodesBlock, {
                          key: 0,
                          class: "container",
                          view: view.value.episodesListView
                        }, null, 8, ["view"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (view.value && view.value.podcastsView && view.value.showEmptyPodcastBlock)
              ? (_openBlock(), _createBlock(EmptyPodcastsBlock, {
                  key: 6,
                  view: view.value.podcastsView,
                  term: view.value.search
                }, null, 8, ["view", "term"]))
              : _createCommentVNode("", true),
            (view.value && view.value.showEmptyBlock)
              ? (_openBlock(), _createElementBlock("section", _hoisted_7, [
                  _createVNode(NoSearchResults, {
                    term: view.value.search
                  }, null, 8, ["term"])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["class"])
      ])
    ]),
    _: 1
  }))
}
}

})